import { loadStripe, Stripe } from '@stripe/stripe-js';

// Replace with your actual Stripe publishable key
// Get from: https://dashboard.stripe.com/apikeys
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_KEY_HERE';

let stripePromise: Promise<Stripe | null>;

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

export const PRODUCT_PRICE = 5.99;
export const PRODUCT_NAME = 'Expense Tracker Premium - Monthly Subscription';
export const BILLING_INTERVAL = 'month';

export interface CheckoutSessionData {
  sessionId: string;
  url: string;
}

// Create checkout session via your backend
export async function createCheckoutSession(): Promise<CheckoutSessionData> {
  try {
    // Call your backend API to create a Stripe checkout session
    const response = await fetch('/.netlify/functions/create-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        productName: PRODUCT_NAME,
        price: PRODUCT_PRICE,
        billingInterval: BILLING_INTERVAL,
        successUrl: `${window.location.origin}/tools/expense-tracker/app?payment=success`,
        cancelUrl: `${window.location.origin}/tools/expense-tracker/app?payment=canceled`,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create checkout session';
    throw new Error(message);
  }
}

// Redirect to Stripe Checkout
export async function redirectToCheckout(): Promise<void> {
  try {
    // Get checkout session from backend
    const { url } = await createCheckoutSession();
    
    // Redirect to Stripe Checkout page
    window.location.href = url;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error occurred';
    alert(`Unable to start checkout: ${message}. Please try again or contact support.`);
  }
}

// Verify payment success from URL parameters
export function checkPaymentStatus(): { status: 'success' | 'canceled' | null; sessionId?: string } {
  const params = new URLSearchParams(window.location.search);
  const payment = params.get('payment');
  const sessionId = params.get('session_id');
  
  if (payment === 'success') {
    return { status: 'success', sessionId: sessionId || undefined };
  } else if (payment === 'canceled') {
    return { status: 'canceled' };
  }
  
  return { status: null };
}

// Retrieve checkout session details from backend
export async function getCheckoutSession(sessionId: string) {
  try {
    const response = await fetch('/.netlify/functions/get-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    });

    if (!response.ok) {
      throw new Error('Failed to retrieve checkout session');
    }

    return await response.json();
  } catch (error) {
    throw new Error('Failed to retrieve payment session. Please contact support.');
  }
}

// Handle successful payment
export async function handlePaymentSuccess(sessionId?: string): Promise<void> {
  
  if (!sessionId) {
    // Fallback without session data
    const fallbackData = {
      isPremium: true,
      purchaseDate: new Date().toISOString(),
      source: 'stripe'
    };
    localStorage.setItem('expense_tracker_premium', JSON.stringify(fallbackData));
    alert('🎉 Welcome to Premium! Payment successful. Please contact support to link your subscription.');
    window.location.reload();
    return;
  }

  try {
    const sessionData = await getCheckoutSession(sessionId);
    
    if (!sessionData || !sessionData.success) {
      throw new Error('Failed to retrieve session data or session not successful');
    }

    if (!sessionData.customerId || !sessionData.subscriptionId) {
      throw new Error('Missing customer ID or subscription ID in session data');
    }

    const premiumData = {
      isPremium: true,
      purchaseDate: new Date().toISOString(),
      source: 'stripe',
      stripeCustomerId: sessionData.customerId,
      subscriptionId: sessionData.subscriptionId,
      subscriptionStatus: sessionData.subscriptionStatus,
      currentPeriodEnd: sessionData.currentPeriodEnd,
      cancelAtPeriodEnd: sessionData.cancelAtPeriodEnd,
    };
    
    localStorage.setItem('expense_tracker_premium', JSON.stringify(premiumData));
    
    // Clear payment status from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('payment');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.toString());
    
    // Show success message
    alert('🎉 Welcome to Premium! You now have full access to all premium features including billing management.');
    
    // Reload to update UI
    window.location.reload();
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    
    // Fallback: grant premium but log warning
    const fallbackData = {
      isPremium: true,
      purchaseDate: new Date().toISOString(),
      source: 'stripe',
      sessionId: sessionId // Store session ID for later recovery
    };
    localStorage.setItem('expense_tracker_premium', JSON.stringify(fallbackData));
    
    alert('🎉 Payment successful! Premium activated. Note: Billing management may require re-login. If issues persist, contact support with session: ' + sessionId.substring(0, 20) + '...');
    window.location.reload();
  }
}
